import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { useSessionStore } from '../auth/session-store';
import { CONTRACT_STATUSES } from '../contracts/api';
import { CUSTOMER_STATUSES, ZONES } from '../customers/api';
import { AppShell } from '../layout/app-shell';
import { MENU_STATUS_FILTERS } from '../menus/api';
import { ZONES as HOLIDAY_ZONES } from '../calendar/api';
import { DELIVERY_NOTE_STATUSES } from '../delivery-notes/api';
import { INVOICE_STATUSES, INVOICE_TYPES } from '../invoices/api';
import { ORDER_SOURCES, ORDER_STATUSES } from '../orders/api';
import { PAYMENT_STATUSES } from '../payments/api';
import { SecurityPage } from './account/security-page';
import { CalendarListPage } from './calendar/calendar-list-page';
import { HolidayListPage } from './calendar/holidays/holiday-list-page';
import { ContractDetailPage } from './contracts/contract-detail-page';
import { ContractFormPage } from './contracts/contract-form-page';
import { ContractListPage } from './contracts/contract-list-page';
import { CustomerDetailPage } from './customers/customer-detail-page';
import { CustomerFormPage } from './customers/customer-form-page';
import { CustomerListPage } from './customers/customer-list-page';
import { DashboardDayPage } from './dashboard/dashboard-day-page';
import { LoginPage } from './login';
import { CategoriesPage } from './menus/categories-page';
import { MenuDetailPage } from './menus/menu-detail-page';
import { MenuFormPage } from './menus/menu-form-page';
import { MenuListPage } from './menus/menu-list-page';
import { GlossaryPage } from './guide/glossary-page';
import { GuideListPage } from './guide/guide-list-page';
import { GuideModulePage } from './guide/guide-module-page';
import { NotificationListPage } from './notifications/notification-list-page';
import { UnsubscribePage } from './notifications/unsubscribe-page';
import { NotificationPreferencesPage } from './account/notification-preferences-page';
import { MyDeliveriesPage } from './deliveries/my-deliveries-page';
import { MyDeliveryDetailPage } from './deliveries/my-delivery-detail-page';
import { DeliveryNoteDetailPage } from './delivery-notes/delivery-note-detail-page';
import { DeliveryNoteListPage } from './delivery-notes/delivery-note-list-page';
import { DeliveryNotePrintBatchPage } from './delivery-notes/delivery-note-print-batch-page';
import { InvoiceDetailPage } from './invoices/invoice-detail-page';
import { InvoiceListPage } from './invoices/invoice-list-page';
import { PaymentDetailPage } from './payments/payment-detail-page';
import { PaymentListPage } from './payments/payment-list-page';
import { RegisterPaymentForm } from './payments/register-payment-form';
import { ArcaConfigPage } from './settings/arca-config-page';
import { BillingConceptConfigPage } from './settings/billing-concept-config-page';
import { EmailSettingsPage } from './settings/email-settings-page';
import { PaymentMethodConfigPage } from './settings/payment-method-config-page';
import { VatRateConfigPage } from './settings/vat-rate-config-page';
import { WithholdingTypeConfigPage } from './settings/withholding-type-config-page';
import { FiscalPointOfSaleConfigPage } from './settings/fiscal-point-of-sale-config-page';
import { PointOfSaleConfigPage } from './settings/point-of-sale-config-page';
import { SettingsHubPage } from './settings/settings-hub-page';
import { UsersListPage } from './settings/users/users-list-page';
import { RolesPage } from './settings/roles/roles-page';
import { SystemParametersPage } from './settings/system-parameters/system-parameters-page';
import { AcceptInvitationPage } from '../auth/accept-invitation-page';
import { ForgotPasswordPage } from '../auth/forgot-password-page';
import { ResetPasswordPage } from '../auth/reset-password-page';
import { ChangeRequestListPage } from './orders/change-request-list-page';
import { OrderDetailPage } from './orders/order-detail-page';
import { OrderFormPage } from './orders/order-form-page';
import { OrderListPage } from './orders/order-list-page';
import { OrderTemplateListPage } from './orders/order-template-list-page';
import { ProductionSheetPrintPage } from './production/production-sheet-print-page';
import { ProductionSheetViewPage } from './production/production-sheet-view-page';
import { todayInArgentina } from '@catering/ui';
import { INTERNAL_USER_STATUSES } from '../users/api';

const rootRoute = createRootRoute();

const loginSearchSchema = z.object({
  reset: z.literal('success').optional().catch(undefined),
  activated: z.literal('success').optional().catch(undefined),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: loginSearchSchema,
  component: LoginPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordSearchSchema = z.object({
  token: z.string().optional().catch(undefined),
});

/** Reached from the recovery email link, no session yet. */
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: resetPasswordSearchSchema,
  component: ResetPasswordPage,
});

const unsubscribeSearchSchema = z.object({
  token: z.string().optional().catch(undefined),
});

/**
 * Reached straight from an email link, no session yet — same reasoning as
 * `loginRoute`, a root-level sibling rather than a child of `protectedRoute`.
 */
const unsubscribeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/notifications/unsubscribe',
  validateSearch: unsubscribeSearchSchema,
  component: UnsubscribePage,
});

const acceptInvitationSearchSchema = z.object({
  token: z.string().optional().catch(undefined),
});

/**
 * `design.md` §5.2 — reached straight from the invitation email link, no
 * session yet, same reasoning as `loginRoute`/`unsubscribeRoute`. No
 * branching by account kind: `EmailInvitationNotifier` already resolved
 * which app's base URL the link points to before the email was sent.
 */
const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invitation',
  validateSearch: acceptInvitationSearchSchema,
  component: AcceptInvitationPage,
});

/**
 * Everything below this route requires a session.
 *
 * The check lives in one place rather than in each page: a guard repeated per
 * screen is a guard that will be forgotten on the screen added next month, and
 * that screen is the one that leaks.
 *
 * This is convenience, not security. The server refuses unauthorised requests
 * on its own; what this prevents is showing an empty shell to somebody who is
 * not signed in.
 */
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  beforeLoad: () => {
    const { isAuthenticated, isRestoring } = useSessionStore.getState();

    // Never redirect while the silent refresh is still running: throwing the
    // user to the login screen for pressing reload is not a security measure.
    if (!isRestoring && !isAuthenticated()) {
      // TanStack Router signals a redirect by throwing its own marker object,
      // which is how it unwinds out of a loader. Not an error, and not a
      // mistake: the rule simply cannot know the framework's convention.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
  component: AppShell,
});

/**
 * List state lives in the URL (`design.md` §7's orchestrator decision):
 * reload/back keep the search, zone, status and page. Invalid or
 * hand-edited values fall back to the default instead of throwing — a
 * stale URL should degrade, not break the page.
 */
const customerListSearchSchema = z.object({
  q: z.string().catch(''),
  zone: z.enum(ZONES).optional().catch(undefined),
  status: z.enum(CUSTOMER_STATUSES).optional().catch(undefined),
  page: z.number().int().min(1).catch(1),
});

const securityRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/account/security',
  component: SecurityPage,
});

const notificationPreferencesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/account/notifications',
  component: NotificationPreferencesPage,
});

const notificationListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/notifications',
  component: NotificationListPage,
});

const customerListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/customers',
  validateSearch: customerListSearchSchema,
  component: CustomerListPage,
});

const customerNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/customers/new',
  component: CustomerFormPage,
});

/**
 * The active tab lives in a validated search param, defaulting to `datos`,
 * so an invalid or hand-edited `tab` degrades instead of breaking the page
 * (same convention as `customerListSearchSchema`).
 */
const customerDetailSearchSchema = z.object({
  tab: z
    .enum(['datos', 'sedes', 'contactos', 'contratos', 'accesos', 'cuenta-corriente', 'antiguedad'])
    .catch('datos'),
});

const customerDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/customers/$customerId',
  validateSearch: customerDetailSearchSchema,
  component: CustomerDetailPage,
});

/**
 * List state lives in the URL, same convention as `customerListSearchSchema`:
 * a stale or hand-edited URL degrades to the default instead of breaking
 * the page.
 */
const menuListSearchSchema = z.object({
  q: z.string().catch(''),
  serviceTypeId: z.string().uuid().optional().catch(undefined),
  dietaryTagId: z.string().uuid().optional().catch(undefined),
  excludeAllergenId: z.string().uuid().optional().catch(undefined),
  status: z.enum(MENU_STATUS_FILTERS).optional().catch(undefined),
  page: z.number().int().min(1).catch(1),
});

const menuListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/menus',
  validateSearch: menuListSearchSchema,
  component: MenuListPage,
});

/**
 * List state lives in the URL, same convention as `customerListSearchSchema`.
 * `apps/api` `GET /contracts` takes no query parameters (`contracts/api.ts`),
 * so filtering happens client-side over the full scoped list; no `page`
 * param, since there is no server-side pagination to reflect.
 */
const contractListSearchSchema = z.object({
  q: z.string().catch(''),
  customerId: z.string().uuid().optional().catch(undefined),
  status: z.enum(CONTRACT_STATUSES).optional().catch(undefined),
});

const contractListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/contracts',
  validateSearch: contractListSearchSchema,
  component: ContractListPage,
});

/**
 * `customerId` preselects the customer when reached from the customer
 * detail's Contratos tab (`design.md` §10.2 amendment); absent, the form
 * behaves as before with an empty selector.
 */
const contractNewSearchSchema = z.object({
  customerId: z.string().uuid().optional().catch(undefined),
});

/**
 * `customerId` preselects the customer when reached from the customer
 * detail's Cuenta corriente tab (`customer-account-statement/spec.md`),
 * same convention as `contractNewSearchSchema`; absent, the form behaves
 * as before with an empty selector.
 */
const paymentNewSearchSchema = z.object({
  customerId: z.string().uuid().optional().catch(undefined),
});

const contractNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/contracts/new',
  validateSearch: contractNewSearchSchema,
  component: ContractFormPage,
});

/**
 * The active tab lives in a validated search param, defaulting to
 * `condiciones`, same convention as `customerDetailSearchSchema`.
 */
const contractDetailSearchSchema = z.object({
  tab: z.enum(['condiciones', 'precios', 'versiones', 'documento']).catch('condiciones'),
});

const contractDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/contracts/$contractId',
  validateSearch: contractDetailSearchSchema,
  component: ContractDetailPage,
});

function todayIso(): string {
  return todayInArgentina();
}

function addDaysIso(date: string, days: number): string {
  const shifted = new Date(`${date}T00:00:00.000Z`);

  shifted.setUTCDate(shifted.getUTCDate() + days);

  return shifted.toISOString().slice(0, 10);
}

/**
 * List state lives in the URL, same convention as `customerListSearchSchema`.
 * `from`/`to` default to the current week (today → +6 days) when absent or
 * invalid, mirroring `menu-calendar/design.md` §6.2. `serviceTypeId` filters
 * client-side (`CalendarFilters`'s doc comment): `GET /calendar` only
 * accepts `menuId`/`positionId`, not `serviceTypeId`. `positionId` reaches
 * `GET /calendar` directly.
 */
const calendarListSearchSchema = z.object({
  from: z.string().catch(() => todayIso()),
  to: z.string().catch(() => addDaysIso(todayIso(), 6)),
  serviceTypeId: z.string().uuid().optional().catch(undefined),
  positionId: z.string().uuid().optional().catch(undefined),
  isPublished: z.boolean().optional().catch(undefined),
});

const calendarListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/calendar',
  validateSearch: calendarListSearchSchema,
  component: CalendarListPage,
});

/**
 * List state lives in the URL, same convention as `calendarListSearchSchema`.
 * `page` follows the `pageSize`-cap convention `GET /holidays` shares with
 * `/customers`/`/menus` (`design.md` §6.2), unlike `/calendar` (date-range
 * width instead of pages).
 */
const holidayListSearchSchema = z.object({
  zone: z.enum(HOLIDAY_ZONES).optional().catch(undefined),
  year: z.coerce.number().int().optional().catch(undefined),
  page: z.coerce.number().int().min(1).catch(1),
});

/**
 * Declared as a static segment before any dynamic `/calendar/$...` route
 * (none exists in this module yet), same resolution criterion as
 * `/menus/categories` vs. `/menus/$menuId`.
 */
const holidayListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/calendar/holidays',
  validateSearch: holidayListSearchSchema,
  component: HolidayListPage,
});

const guideListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/guia',
  component: GuideListPage,
});

/**
 * Declared as a static segment before the dynamic `/guia/$moduleId`, same
 * resolution criterion as `/menus/categories` vs. `/menus/$menuId`: `glosario`
 * must win the match, never be treated as a `moduleId`.
 */
const glossaryRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/guia/glosario',
  component: GlossaryPage,
});

/**
 * `$moduleId` is not restricted with `z.enum` because `GUIDE_MODULES` grows
 * with every new module: an unrecognised id is not a routing 404, it is
 * "module without a guide yet" — `GuideModulePage` decides what to render
 * (`EmptyState`), same criterion `calendarListSearchSchema` uses `.catch()`
 * instead of rejecting the value.
 */
const guideModuleRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/guia/$moduleId',
  component: GuideModulePage,
});

const menuNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/menus/new',
  component: MenuFormPage,
});

/**
 * The active tab lives in a validated search param, defaulting to
 * `informacion`, same convention as `customerDetailSearchSchema`.
 */
const menuDetailSearchSchema = z.object({
  tab: z.enum(['informacion', 'etiquetas', 'alergenos', 'notas']).catch('informacion'),
});

const menuDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/menus/$menuId',
  validateSearch: menuDetailSearchSchema,
  component: MenuDetailPage,
});

/**
 * Declared before `menuDetailRoute` is matched by TanStack Router
 * automatically (static segments win over dynamic `$menuId`), same
 * resolution as `/customers/new` vs. `/customers/$customerId` — order in
 * `addChildren` below does not need to change for this, but it is listed
 * next to `menuDetailRoute` for readability.
 */
const menuCategoriesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/menus/categories',
  component: CategoriesPage,
});

/**
 * `day-board/spec.md` → "Pantalla del Tablero del día en back-office".
 * The service date lives in the URL, default today, same convention as
 * `calendarListSearchSchema`.
 */
const dashboardSearchSchema = z.object({
  serviceDate: z
    .string()
    .date()
    .catch(() => todayIso()),
  /**
   * `day-board/spec.md` → KPI cards filter the entries the board already
   * loaded, client-side (design.md §6) — there is no `hasOrder`/`hasAlerts`
   * filter on `GET /orders`, same reasoning as `contractListSearchSchema`.
   */
  view: z.enum(['all', 'missing', 'draft', 'alerts']).catch('all'),
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  validateSearch: dashboardSearchSchema,
  component: DashboardDayPage,
});

/**
 * List state lives in the URL, same convention as `customerListSearchSchema`.
 * Unlike `/contracts`, `GET /orders` paginates for real (`design.md` §7):
 * `pageSize` is itself a URL param, clamped to `MAX_ORDER_PAGE_SIZE` in
 * `orders/api.ts` before the request ever leaves the browser.
 */
const orderListSearchSchema = z.object({
  serviceDate: z
    .string()
    .date()
    .catch(() => todayIso()),
  customerId: z.string().uuid().optional().catch(undefined),
  siteId: z.string().uuid().optional().catch(undefined),
  status: z.enum(ORDER_STATUSES).optional().catch(undefined),
  source: z.enum(ORDER_SOURCES).optional().catch(undefined),
  page: z.number().int().min(1).catch(1),
  pageSize: z.number().int().min(1).max(100).catch(20),
});

const orderListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders',
  validateSearch: orderListSearchSchema,
  component: OrderListPage,
});

/**
 * Declared before `orderDetailRoute` so the static segment wins over the
 * dynamic `$orderId`, same resolution criterion as `/contracts/new` vs.
 * `/contracts/$contractId`.
 */
const orderNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders/new',
  component: OrderFormPage,
});

/**
 * Declared before `orderDetailRoute` so the static segment wins over the
 * dynamic `$orderId`, same resolution criterion as `orderNewRoute`
 * (`design.md` §8.1).
 */
const orderTemplateListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders/templates',
  component: OrderTemplateListPage,
});

/**
 * List state lives in the URL, same convention as `orderListSearchSchema`.
 * `status` accepts an explicit `'ALL'` sentinel alongside the four real
 * `OrderChangeRequest` statuses: the default view prioritizes `PENDING`
 * (`design.md` §8.2), but a terminal status like `APPLIED` must still be
 * reachable by an explicit choice — `undefined` would ask the API for
 * every status, `'ALL'` maps to that `undefined` in `ChangeRequestListPage`
 * (`tasks.md` PR 6 TRIANGULATE).
 */
const changeRequestListSearchSchema = z.object({
  status: z.enum(['ALL', 'PENDING', 'APPROVED', 'REJECTED', 'APPLIED']).catch('PENDING'),
  page: z.number().int().min(1).catch(1),
  pageSize: z.number().int().min(1).max(100).catch(20),
});

const changeRequestListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders/change-requests',
  validateSearch: changeRequestListSearchSchema,
  component: ChangeRequestListPage,
});

/**
 * The active tab lives in a validated search param, defaulting to
 * `detalle`, same convention as `contractDetailSearchSchema`.
 */
const orderDetailSearchSchema = z.object({
  tab: z.enum(['detalle', 'historial', 'cambios']).catch('detalle'),
});

const orderDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders/$orderId',
  validateSearch: orderDetailSearchSchema,
  component: OrderDetailPage,
});

/**
 * List state lives in the URL, same convention as `orderListSearchSchema`.
 * `sinFirmar` is a quick filter that maps to `status=DELIVERED` in
 * `DeliveryNoteListPage` (`design.md` §9.1) — it is not a real API param on
 * its own.
 */
const deliveryNoteListSearchSchema = z.object({
  serviceDate: z
    .string()
    .date()
    .catch(() => todayIso()),
  siteId: z.string().uuid().optional().catch(undefined),
  status: z.enum(DELIVERY_NOTE_STATUSES).optional().catch(undefined),
  sinFirmar: z.boolean().catch(false),
});

const deliveryNoteListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/delivery-notes',
  validateSearch: deliveryNoteListSearchSchema,
  component: DeliveryNoteListPage,
});

/**
 * Declared before `deliveryNoteDetailRoute` so the static segment wins over
 * the dynamic `$id`, same resolution criterion as `orderNewRoute`.
 */
const deliveryNotePrintBatchRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/delivery-notes/print-batch',
  component: DeliveryNotePrintBatchPage,
});

const myDeliveriesSearchSchema = z.object({
  fecha: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
});

/** `offline-signature` S5: phone-first screen for `deliverynote.sign`. */
const myDeliveriesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/entregas',
  validateSearch: myDeliveriesSearchSchema,
  component: MyDeliveriesPage,
});

const myDeliveryDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/entregas/$id',
  component: MyDeliveryDetailPage,
});

const deliveryNoteDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/delivery-notes/$id',
  component: DeliveryNoteDetailPage,
});

/**
 * List state lives in the URL, same convention as `deliveryNoteListSearchSchema`.
 * `invoiceType` has no server-side filter on `GET /invoices`
 * (`invoice.dto.ts`'s `listInvoicesQuerySchema`): `InvoiceListPage`
 * filters it client-side over the fetched page.
 */
const invoiceListSearchSchema = z.object({
  status: z.enum(INVOICE_STATUSES).optional().catch(undefined),
  customerId: z.string().uuid().optional().catch(undefined),
  periodStart: z.string().date().optional().catch(undefined),
  periodEnd: z.string().date().optional().catch(undefined),
  invoiceType: z.enum(INVOICE_TYPES).optional().catch(undefined),
});

const invoiceListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/invoices',
  validateSearch: invoiceListSearchSchema,
  component: InvoiceListPage,
});

const invoiceDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/invoices/$id',
  component: InvoiceDetailPage,
});

/**
 * List state lives in the URL, same convention as `invoiceListSearchSchema`.
 */
const paymentListSearchSchema = z.object({
  status: z.enum(PAYMENT_STATUSES).optional().catch(undefined),
  customerId: z.string().uuid().optional().catch(undefined),
  fromDate: z.string().date().optional().catch(undefined),
  toDate: z.string().date().optional().catch(undefined),
});

const paymentListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/payments',
  validateSearch: paymentListSearchSchema,
  component: PaymentListPage,
});

/**
 * Declared before `paymentDetailRoute` so the static segment wins over the
 * dynamic `$id`, same resolution criterion as `orderNewRoute`.
 */
const paymentNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/payments/new',
  validateSearch: paymentNewSearchSchema,
  component: RegisterPaymentForm,
});

const paymentDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/payments/$id',
  component: PaymentDetailPage,
});

const arcaConfigRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/arca-config',
  component: ArcaConfigPage,
});

const fiscalPointsOfSaleRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/fiscal-points-of-sale',
  component: FiscalPointOfSaleConfigPage,
});

const pointsOfSaleRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/points-of-sale',
  component: PointOfSaleConfigPage,
});

const billingConceptsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/billing-concepts',
  component: BillingConceptConfigPage,
});

const vatRatesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/vat-rates',
  component: VatRateConfigPage,
});

const paymentMethodsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/payment-methods',
  component: PaymentMethodConfigPage,
});

const withholdingTypesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/withholding-types',
  component: WithholdingTypeConfigPage,
});

const emailSettingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/email',
  component: EmailSettingsPage,
});

const settingsHubRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings',
  component: SettingsHubPage,
});

const usersListSearchSchema = z.object({
  search: z.string().optional().default(''),
  status: z.enum(INTERNAL_USER_STATUSES).optional().catch(undefined),
  roleId: z.string().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().default(1).catch(1),
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/users',
  validateSearch: usersListSearchSchema,
  component: UsersListPage,
});

const settingsRolesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/roles',
  component: RolesPage,
});

const settingsSystemParametersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/system-parameters',
  component: SystemParametersPage,
});

/**
 * The active tab lives in a validated search param, defaulting to
 * `consolidado`, same convention as `contractDetailSearchSchema`.
 */
const productionViewSearchSchema = z.object({
  tab: z.enum(['consolidado', 'tipo-servicio', 'sede']).catch('consolidado'),
});

/** No fecha in the URL means nothing to show — redirects to today, same criterion `dashboardSearchSchema` already applies. */
const productionRedirectRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/production',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/production/$date', params: { date: todayIso() } });
  },
});

const productionViewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/production/$date',
  validateSearch: productionViewSearchSchema,
  component: ProductionSheetViewPage,
});

/**
 * A second, sibling pathless route group (`print-protected`): the same
 * session guard as `protectedRoute`, but `component: () => <Outlet />`
 * instead of `AppShell` — the print page needs auth without the
 * sidebar/header chrome (`production-export/spec.md` → "sin AppShell
 * alrededor"). Both groups are pathless (no `path`, only `id`), so they
 * do not add a URL segment and their children never collide: `/production/$date`
 * (this group's sibling) and `/production/$date/print` (this group's own
 * child) are already distinct URLs on their own.
 */
const printProtectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'print-protected',
  beforeLoad: () => {
    const { isAuthenticated, isRestoring } = useSessionStore.getState();

    if (!isRestoring && !isAuthenticated()) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
  component: Outlet,
});

const productionPrintRoute = createRoute({
  getParentRoute: () => printProtectedRoute,
  path: '/production/$date/print',
  component: ProductionSheetPrintPage,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  unsubscribeRoute,
  acceptInvitationRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    orderListRoute,
    orderNewRoute,
    orderTemplateListRoute,
    changeRequestListRoute,
    orderDetailRoute,
    deliveryNoteListRoute,
    deliveryNotePrintBatchRoute,
    deliveryNoteDetailRoute,
    myDeliveriesRoute,
    myDeliveryDetailRoute,
    invoiceListRoute,
    invoiceDetailRoute,
    paymentListRoute,
    paymentNewRoute,
    paymentDetailRoute,
    arcaConfigRoute,
    fiscalPointsOfSaleRoute,
    pointsOfSaleRoute,
    billingConceptsRoute,
    vatRatesRoute,
    paymentMethodsRoute,
    withholdingTypesRoute,
    emailSettingsRoute,
    settingsHubRoute,
    settingsUsersRoute,
    settingsRolesRoute,
    settingsSystemParametersRoute,
    productionRedirectRoute,
    productionViewRoute,
    securityRoute,
    notificationPreferencesRoute,
    notificationListRoute,
    customerListRoute,
    customerNewRoute,
    customerDetailRoute,
    contractListRoute,
    contractNewRoute,
    contractDetailRoute,
    menuListRoute,
    menuNewRoute,
    menuCategoriesRoute,
    menuDetailRoute,
    calendarListRoute,
    holidayListRoute,
    guideListRoute,
    glossaryRoute,
    guideModuleRoute,
  ]),
  printProtectedRoute.addChildren([productionPrintRoute]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
